/**
 * Affiliate referral attribution helper.
 *
 * On page load, if `?ref=SK-XXXXXX` is in the URL, persist it to
 * localStorage so it survives across the login/register flow.
 * Lifetime attribution — once stored, it sticks for 90 days
 * (long enough to outlast checkout + email confirmation cycles).
 */

const STORAGE_KEY = 'smartkick_ref';
const TTL_DAYS = 90;
const API_URL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : `${window.location.origin}/api`;

export function captureRefFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('ref');
    if (!raw) return;
    const code = raw.toUpperCase().trim();
    // Accept legacy SK- codes AND custom vanity codes (e.g. UTDKHARE).
    // Basic sanity check only — the server validates that the code maps to
    // an approved partner before we persist it.
    if (!code || code.length < 3 || code.length > 32 || !/^[A-Z0-9-]+$/.test(code)) return;

    // Server-side validate + track click; if invalid, don't persist.
    fetch(`${API_URL}/partners/track/${encodeURIComponent(code)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.ok) return;
        const record = {
          code,
          partner_name: data.partner_name || null,
          captured_at: Date.now(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
      })
      .catch(() => null);
  } catch (e) {
    // localStorage may be blocked — non-fatal
  }
}

function readRecord() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw);
    if (!rec?.code || !rec?.captured_at) return null;
    const ageDays = (Date.now() - rec.captured_at) / 86400000;
    if (ageDays > TTL_DAYS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return rec;
  } catch {
    return null;
  }
}

export function getRefCode() {
  return readRecord()?.code || null;
}

export function getRefPartnerName() {
  return readRecord()?.partner_name || null;
}

export function clearRefCode() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ========================================================================
// Member-get-member referral (/r/CODE). Kept separate from the influencer
// affiliate code above so the two programmes never collide.
// ========================================================================
const MEMBER_KEY = 'smartkick_mref';

export function captureMemberRef(rawCode) {
  try {
    const code = (rawCode || '').toUpperCase().trim();
    if (!code || code.length < 4 || code.length > 16 || !/^[A-Z0-9]+$/.test(code)) {
      return Promise.resolve(null);
    }
    return fetch(`${API_URL}/referrals/track/${encodeURIComponent(code)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.ok) return null;
        const record = { code, referrer_name: data.referrer_name || null, captured_at: Date.now() };
        localStorage.setItem(MEMBER_KEY, JSON.stringify(record));
        return record;
      })
      .catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

function readMemberRecord() {
  try {
    const raw = localStorage.getItem(MEMBER_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw);
    if (!rec?.code || !rec?.captured_at) return null;
    if ((Date.now() - rec.captured_at) / 86400000 > TTL_DAYS) {
      localStorage.removeItem(MEMBER_KEY);
      return null;
    }
    return rec;
  } catch {
    return null;
  }
}

export function getMemberRefCode() {
  return readMemberRecord()?.code || null;
}

export function getMemberRefName() {
  return readMemberRecord()?.referrer_name || null;
}

export function clearMemberRefCode() {
  try {
    localStorage.removeItem(MEMBER_KEY);
  } catch {
    // ignore
  }
}
